import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, IconButton,
  InputLabel, MenuItem, Paper, Select, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, Tabs, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, ArrowBack as BackIcon, Delete as DeleteIcon,
  Edit as EditIcon, Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import menuService from '../../../../services/menuService';
import menuItemService from '../../../../services/menuItemService';
import menuSectionService from '../../../../services/menuSectionService';
import type { Menu as MenuDoc, MenuItem as MenuItemDoc, MenuItemKind, MenuSection, MenuStatus, MenuType, MenuVisibility } from '../../../../types/menu';
import MenuSectionsPanel from '../../../../components/venue/MenuSectionsPanel';
import MenuPreviewPanel from '../../../../components/venue/MenuPreviewPanel';
import MenuPublishPanel from '../../../../components/venue/MenuPublishPanel';
// MENU-EXEC.2 — Serving sizes (MenuFormatsPanel) and Recipes (MenuRecipesPanel)
// tabs are hidden: menu_formats / menu_consumption_recipes are inert for POS
// execution (POS uses InventoryItem.executionBehavior + InventoryItemRecipe).
// The panel components and their services/routes are retained, just unrendered.
import { menuTypeLabel } from './menuTypeLabels';
import type { MenuRenderResponse } from '../../../../types/menu';

const STATUS_COLORS: Record<MenuStatus, 'default' | 'primary' | 'success' | 'warning'> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  ARCHIVED: 'warning',
};

/**
 * Menu types where the primary item kind is WINE. Drives the "Add Wine"
 * button label + ?kind=WINE routing, and exposes the Producer/Vintage table
 * columns. Mirrors the WINE_INFERRED_MENU_TYPES list in MenuItemForm dispatcher.
 */
const WINE_MENU_TYPES: MenuType[] = ['FULL', 'BY_THE_GLASS', 'RESERVE', 'EVENT', 'PRIVATE'];
const isWineMenuType = (type: MenuType): boolean => WINE_MENU_TYPES.includes(type);

// Phase G.4.c — non-wine menu type → suggested item kind. Used ONLY to
// preselect the closest preset in the add-item picker (suggestedAddKind); it
// never restricts what can be added. Mirrors MenuItemForm's MENU_TYPE_TO_ITEM_KIND
// (incl. the SPIRITS→SPIRIT rename). TASTING stays absent (still gated).
const MENU_TYPE_SUGGESTED_KIND: Partial<Record<MenuType, MenuItemKind>> = {
  COCKTAIL: 'COCKTAIL',
  FOOD: 'DISH',
  BEER: 'BEER',
  SPIRITS: 'SPIRIT',
  RETAIL: 'RETAIL',
  OTHER: 'OTHER',
};

// Phase G.4.a — universal add-item presets, shown by the picker on EVERY
// authorable menu (not just MIXED). Presets are helpers that set the temporary
// backend-compatibility kind + the generic form's defaults; they are NOT
// separate item architectures. TASTING_MENU is intentionally excluded (no
// authorable kind yet — pending choice-group/composition design).
const ADD_ITEM_PRESETS: { kind: MenuItemKind; label: string }[] = [
  { kind: 'WINE', label: 'Wine' },
  { kind: 'COCKTAIL', label: 'Cocktail' },
  { kind: 'DISH', label: 'Dish' },
  { kind: 'BEER', label: 'Beer' },
  { kind: 'SPIRIT', label: 'Spirit' },
  { kind: 'RETAIL', label: 'Retail' },
  { kind: 'OTHER', label: 'Other item' },
];

// MENU-EXEC.2 — 'formats' (Serving sizes) and 'recipes' tabs removed.
type TabKey = 'items' | 'sections' | 'preview' | 'share';

const thStyle = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
};

const MenuDetail: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { menuId } = useParams<{ menuId: string }>();
  const [searchParams] = useSearchParams();
  const { venueId, defaultCurrency } = useVenue();
  const base = `/admin/venues/${venueId}/menus`;

  const [menu, setMenu] = useState<MenuDoc | null>(null);
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError] = useState('');
  // Initial tab can be deep-linked via ?tab=. Stale links to removed tabs
  // (?tab=formats / ?tab=recipes) fall back to 'items'. Defaults to 'items'.
  const [tab, setTab] = useState<TabKey>(() => {
    const requested = searchParams.get('tab');
    const valid: TabKey[] = ['items', 'sections', 'preview', 'share'];
    return requested && valid.includes(requested as TabKey) ? (requested as TabKey) : 'items';
  });

  // Items state — only loaded while the Items tab is active (or has been
  // visited at least once). Section name lookup uses the loaded sections.
  const [items, setItems] = useState<MenuItemDoc[]>([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsError, setItemsError] = useState('');
  const [itemsPagination, setItemsPagination] = useState({ page: 0, pageSize: 25, totalItems: 0 });
  const [search, setSearch] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<MenuVisibility | ''>('');

  // Sections — used to display section name in the items table.
  const [sections, setSections] = useState<MenuSection[]>([]);
  const sectionsById = useMemo(
    () => new Map(sections.map(s => [s._id, s])),
    [sections],
  );

  // MIXED-menu kind picker dialog.
  const [pickKindOpen, setPickKindOpen] = useState(false);

  // Single render call gives us the full menu (items per section + visibility +
  // inventory links). Powers the menu summary strip and setup warnings. Failure
  // is non-blocking — the rest of MenuDetail works fine without it.
  const [renderData, setRenderData] = useState<MenuRenderResponse | null>(null);

  // ── Load menu ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!menuId) return;
    setMenuLoading(true);
    setMenuError('');
    menuService.getMenu(venueId, menuId)
      .then(setMenu)
      .catch(() => setMenuError('Could not load menu. Please try again.'))
      .finally(() => setMenuLoading(false));
  }, [venueId, menuId]);

  // ── Load sections (lightweight, used for items table lookup) ───────────────
  useEffect(() => {
    if (!menuId) return;
    menuSectionService.getSections(venueId, menuId)
      .then(setSections)
      .catch(() => { /* non-blocking */ });
  }, [venueId, menuId]);

  // ── Phase 4.J: Load render data (for recipe-link summaries) ────────────────
  // One call per menu mount. Fails silently so the rest of the page works
  // when the menu is ARCHIVED (render endpoint refuses archived) or when the
  // operator is offline.
  useEffect(() => {
    if (!menuId) return;
    menuService.renderMenu(venueId, menuId)
      .then(setRenderData)
      .catch(() => { /* non-blocking — recipe summaries simply omit */ });
  }, [venueId, menuId]);

  // Menu-wide setup summary. Derived ONLY from already-loaded data: `renderData`
  // (full menu render) and `sections`. Null while render data is loading or when
  // the render endpoint refused the menu (e.g., ARCHIVED — see Phase 4.B).
  // MENU-EXEC.2 — serving-sizes / recipes-linked stats removed (those surfaces
  // are hidden as inert for POS execution).
  const menuSummary = useMemo(() => {
    if (!renderData) return null;
    const allItems = renderData.sections.flatMap(s => s.items ?? []);
    const totalItems = allItems.length;
    const hiddenItems = allItems.filter(i => i.visibility === 'HIDDEN').length;
    const visibleItems = totalItems - hiddenItems;
    return {
      totalItems,
      visibleItems,
      hiddenItems,
      sectionsCount: sections.length,
    };
  }, [renderData, sections]);

  // Phase 4.N — lightweight setup warnings. Informational only, never
  // blocking. Each string is rendered as a bullet in the Alert below the
  // summary strip; the Alert hides entirely when the list is empty.
  // Derived from the same already-loaded data as the summary.
  const menuWarnings = useMemo<string[]>(() => {
    if (!renderData || !menu) return [];
    const warnings: string[] = [];
    const allItems = renderData.sections.flatMap(s => s.items ?? []);
    const wines = allItems.filter(i => i.kind === 'WINE');

    // MENU-EXEC.2 — serving-size / recipe-linked warnings removed: those
    // surfaces (menu_formats / menu_consumption_recipes) are inert for POS and
    // hidden. Execution recipes live on the InventoryItem, not the menu.
    const winesMissingInventory = wines.filter(w => !w.inventoryItemId).length;
    if (winesMissingInventory > 0) {
      warnings.push(
        `${winesMissingInventory} wine item${winesMissingInventory === 1 ? ' is' : 's are'} missing an inventory link.`,
      );
    }
    if (sections.length === 0) {
      warnings.push('This menu has no sections yet.');
    }
    if (allItems.length > 0 && allItems.every(i => i.visibility === 'HIDDEN')) {
      warnings.push('This menu has no visible items yet.');
    }
    // Phase G.4.b — menu-type↔item-kind "mismatch" warnings removed: any menu
    // can hold any generic item, so an item's kind is never wrong for a menu.
    return warnings;
  }, [renderData, menu, sections]);

  // ── Load items (paginated, server-side) ────────────────────────────────────
  const fetchItems = useCallback(
    async (page: number, pageSize: number, vis: MenuVisibility | '') => {
      if (!menuId) return;
      setItemsLoading(true);
      setItemsError('');
      try {
        const result = await menuItemService.getItems(
          venueId,
          menuId,
          page + 1,
          pageSize,
          vis ? { visibility: vis } : undefined,
        );
        setItems(result.data);
        setItemsPagination(prev => ({ ...prev, page, pageSize, totalItems: result.pagination.totalItems }));
      } catch {
        setItemsError('Could not load items. Please try again.');
      } finally {
        setItemsLoading(false);
      }
    },
    [venueId, menuId],
  );

  useEffect(() => {
    fetchItems(itemsPagination.page, itemsPagination.pageSize, visibilityFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPagination.page, itemsPagination.pageSize, visibilityFilter, menuId]);

  // Client-side search filter (display name + subtitle + wine producer when present).
  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter(item => {
      const name = item.displaySnapshot?.name?.toLowerCase() ?? '';
      const sub = item.displaySnapshot?.subtitle?.toLowerCase() ?? '';
      const producer = item.wineDetails?.wineDisplay?.producerName?.toLowerCase() ?? '';
      return name.includes(q) || sub.includes(q) || (producer !== '' && producer.includes(q));
    });
  }, [items, search]);

  const handleDeleteItem = async (e: React.MouseEvent, item: MenuItemDoc) => {
    e.stopPropagation();
    const itemName = item.displaySnapshot?.name ?? 'this item';
    if (!window.confirm(`Delete "${itemName}"? This cannot be undone.`)) return;
    try {
      await menuItemService.deleteItem(venueId, item.menuId, item._id);
      fetchItems(itemsPagination.page, itemsPagination.pageSize, visibilityFilter);
    } catch {
      setItemsError(`Could not delete "${itemName}". Please try again.`);
    }
  };

  const formatItemPrice = (item: MenuItemDoc): string => {
    if (item.basePrice == null) return '—';
    const code = item.currency ?? defaultCurrency ?? '';
    return code ? `${code} ${item.basePrice.toFixed(2)}` : item.basePrice.toFixed(2);
  };

  // ── Add-item UX (Phase G.4.a — one generic flow for every menu) ────────────
  // Every authorable menu shows a single "Add item" button that opens the
  // universal preset picker. Menu type no longer selects a different button or
  // restricts what can be added — it only *suggests* the closest preset inside
  // the picker (see suggestedAddKind). TASTING stays gated (no authorable kind
  // yet, pending choice-group/composition design); archived menus stay disabled.
  const addButtonLabel = 'Add item';
  const addButtonGatedByType = !!menu && menu.type === 'TASTING';
  const addButtonDisabled = !menu || menu.status === 'ARCHIVED' || addButtonGatedByType;
  const addButtonTooltip = addButtonGatedByType
    ? 'Tasting menu item authoring is coming soon. This menu type may need course and pairing structure, so it is not enabled yet.'
    : '';

  // Menu type silently suggests the closest preset; it never restricts the
  // options. MIXED (and the gated TASTING) have no suggestion. Reuses
  // isWineMenuType() and the MENU_TYPE_SUGGESTED_KIND mapping defined at the top.
  const suggestedAddKind: MenuItemKind | null = !menu
    ? null
    : isWineMenuType(menu.type)
      ? 'WINE'
      : MENU_TYPE_SUGGESTED_KIND[menu.type] ?? null;

  // Producer/Vintage columns are derived from actual item content, not menu
  // type (Phase G.4.b): show them when the menu contains at least one wine item
  // (kind === 'WINE' or carrying wineDetails). Prefer the full render data so
  // the columns aren't dependent on the current pagination page; fall back to
  // the paginated items when renderData is unavailable (archived / offline).
  const hasWineItems = renderData
    ? renderData.sections.some(s => (s.items ?? []).some(i => i.kind === 'WINE' || !!i.wineDetails))
    : items.some(i => i.kind === 'WINE' || !!i.wineDetails);
  const showWineColumns = !!menu && hasWineItems;

  // Always open the universal preset picker. Each preset navigates to the same
  // /add?kind=<KIND> contract the MenuItemForm dispatcher reads, so existing
  // direct deep links keep working unchanged.
  const handleAddClick = () => {
    if (!menu || addButtonDisabled) return;
    setPickKindOpen(true);
  };

  // ── Early returns ──────────────────────────────────────────────────────────
  if (menuLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (menuError || !menu) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 1, mb: 2 }}>
          {menuError || 'Menu not found.'}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(base)} sx={{ textTransform: 'none' }}>
          Back to menus
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header card */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(base)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Menus
          </Button>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={600} noWrap>{menu.name}</Typography>
              <Chip label={menuTypeLabel(menu.type)} size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
              <Chip label={menu.status} size="small" color={STATUS_COLORS[menu.status]} sx={{ fontSize: '0.7rem', height: 22 }} />
            </Box>
            {menu.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {menu.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => navigate(`${base}/edit/${menu._id}`)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              disabled={menu.status === 'ARCHIVED'}
            >
              Edit
            </Button>
            <Tooltip title={addButtonTooltip} placement="bottom" disableHoverListener={!addButtonTooltip}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                  disabled={addButtonDisabled}
                >
                  {addButtonLabel}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Phase 4.N — operational summary strip + setup warnings.
          Lives between the header and the tabs so the operator gets a
          one-glance readout of menu setup state without opening any tab.
          Both blocks are derived from already-loaded data; nothing here
          triggers a backend call. The strip is hidden while renderData is
          still loading or unavailable (e.g., for ARCHIVED menus where the
          render endpoint refuses) so we never display misleading zeros. */}
      {menuSummary && (
        <Paper sx={{ borderRadius: 2, p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              rowGap: 1.5,
              columnGap: 3,
            }}
          >
            {[
              { label: 'Items',         value: menuSummary.totalItems },
              { label: 'Visible',       value: menuSummary.visibleItems },
              { label: 'Hidden',        value: menuSummary.hiddenItems },
              { label: 'Sections',      value: menuSummary.sectionsCount },
            ].map(stat => (
              <Box key={stat.label} sx={{ minWidth: 96 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                >
                  {stat.label}
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
                  {stat.value}
                </Typography>
              </Box>
            ))}
          </Box>
          {menuWarnings.length > 0 && (
            <Alert
              severity="info"
              icon={false}
              sx={{
                mt: 2,
                borderRadius: 1,
                py: 0.5,
                '& .MuiAlert-message': { py: 0.5 },
              }}
            >
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {menuWarnings.map((w, i) => (
                  <Typography
                    component="li"
                    key={i}
                    variant="caption"
                    sx={{ display: 'list-item', lineHeight: 1.6 }}
                  >
                    {w}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v as TabKey)}
          sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, px: 2 }}
        >
          {/* MENU-EXEC.2 — "Serving sizes" and "Recipes" tabs removed (inert
              for POS; execution lives on InventoryItem). */}
          <Tab label="Items" value="items" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Sections" value="sections" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Preview" value="preview" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Share" value="share" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>

        {tab === 'items' && (
          <Box>
            {/* Phase 7.C.3b — Tasting menus are gated: item authoring is not
                enabled yet (may need course/pairing structure). Coming-soon
                banner instead of a broken-looking empty Add. */}
            {menu?.type === 'TASTING' && (
              <Box sx={{ px: 3, pt: 2 }}>
                <Alert severity="info" sx={{ borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                    Tasting menu setup is coming soon.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Future support may include courses, pairings and menu-level stock/recipe setup. Use Food, Wine, Cocktail or Mixed menus for current item authoring.
                  </Typography>
                </Alert>
              </Box>
            )}
            {/* MENU-EXEC.2 — removed the "Go to Serving sizes" prerequisite hint
                and the serving-sizes/recipes hierarchy caption: those surfaces
                are hidden, so the guidance would dead-end on a removed tab. */}
            {/* Filters */}
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
              <TextField
                size="small"
                placeholder="Search name, subtitle, or producer"
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ flex: 1, minWidth: 220, maxWidth: 360 }}
                slotProps={{ input: { startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> } }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={visibilityFilter}
                  label="Visibility"
                  onChange={e => {
                    setVisibilityFilter(e.target.value as MenuVisibility | '');
                    setItemsPagination(p => ({ ...p, page: 0 }));
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="LIVE">LIVE</MenuItem>
                  <MenuItem value="HIDDEN">HIDDEN</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ flex: 1 }} />
              <Tooltip title={addButtonTooltip} placement="bottom" disableHoverListener={!addButtonTooltip}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    disabled={addButtonDisabled}
                  >
                    {addButtonLabel}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {itemsError && (
              <Box sx={{ px: 3, pt: 2 }}>
                <Alert severity="error" onClose={() => setItemsError('')} sx={{ borderRadius: 1 }}>{itemsError}</Alert>
              </Box>
            )}

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Name</TableCell>
                    {showWineColumns && (
                      <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Producer</TableCell>
                    )}
                    {showWineColumns && (
                      <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Vintage</TableCell>
                    )}
                    <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Subtitle</TableCell>
                    <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Section</TableCell>
                    <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Visibility</TableCell>
                    <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }} align="right">Base Price</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemsLoading ? (
                    <TableRow>
                      <TableCell colSpan={showWineColumns ? 8 : 6}>
                        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                          <CircularProgress size={28} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={showWineColumns ? 8 : 6}>
                        <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            {search || visibilityFilter ? 'No items match the current filters.' : 'No items yet.'}
                          </Typography>
                          {!search && !visibilityFilter && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 460, mx: 'auto', mb: 1 }}>
                              {/* Phase G.4.b — generic empty-state copy. Item
                                  authoring is one generic flow, so the guidance
                                  no longer branches on menu type / item kind. */}
                              Add your first item to begin building the menu.
                            </Typography>
                          )}
                          {!search && !visibilityFilter && !addButtonDisabled && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={handleAddClick}
                              sx={{ mt: 1, textTransform: 'none' }}
                            >
                              {addButtonLabel}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map(item => {
                      const sectionName = item.sectionId
                        ? sectionsById.get(item.sectionId)?.name ?? '—'
                        : 'Unsectioned';
                      // MENU-EXEC.2 — serving-size / recipe-link indicators
                      // removed from the items table. They derived from the
                      // inert menu_consumption_recipes (consumptionRecipeId) and
                      // menu_formats surfaces, which are hidden in this phase.
                      // Execution recipes live on the InventoryItem, not here.
                      const visibilityWord = item.visibility === 'HIDDEN' ? 'hidden' : 'visible';
                      const hasInventoryLink = !!item.inventoryItemId;
                      const inventoryPart = hasInventoryLink ? 'inventory linked' : 'no inventory link';

                      // Quick-status chips. Capped at 2 per row. Operational
                      // state first, then Hidden. WINE surfaces inventory-link
                      // state; other kinds only surface Hidden.
                      const statusChips: Array<{ label: string; color: 'success' | 'warning' | 'default' }> = [];
                      if (item.kind === 'WINE') {
                        if (!hasInventoryLink) {
                          statusChips.push({ label: 'Missing inventory', color: 'warning' });
                        } else if (item.visibility !== 'HIDDEN') {
                          statusChips.push({ label: 'Inventory linked', color: 'success' });
                        }
                      }
                      if (item.visibility === 'HIDDEN') {
                        statusChips.push({ label: 'Hidden', color: 'default' });
                      }
                      const visibleStatusChips = statusChips.slice(0, 2);
                      let operationalSummary = '';
                      if (item.kind === 'WINE') {
                        const bottleSize = item.wineDetails?.bottleSize;
                        // For wine, surface bottle size and inventory link as
                        // separate parts when both are present (per Phase 4.M
                        // spec). Examples:
                        //   "750 ml · inventory linked · visible"
                        //   "Bottle linked · inventory linked · hidden"  (no recorded size)
                        //   "no inventory link · visible"                (neither)
                        const sizePart = bottleSize ? `${bottleSize} ml` : null;
                        const linkPart = bottleSize == null && hasInventoryLink
                          ? 'Bottle linked'
                          : inventoryPart;
                        operationalSummary = [sizePart, linkPart, visibilityWord]
                          .filter((s): s is string => !!s).join(' · ');
                      }
                      return (
                        <TableRow
                          key={item._id}
                          hover
                          onClick={() => navigate(`${base}/${menu._id}/edit/${item._id}`)}
                          sx={{ cursor: 'pointer', opacity: item.visibility === 'HIDDEN' ? 0.6 : 1 }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              {item.kind === 'WINE' && (
                                <Chip label="Wine" size="small" sx={{ fontSize: '0.62rem', height: 18, fontFamily: 'monospace' }} />
                              )}
                              <Typography variant="body2" fontWeight={600}>
                                {item.displaySnapshot?.name ?? '(unnamed)'}
                              </Typography>
                              {/* Quick-status chips. Max 2 per row, picked
                                  operationally first then Hidden. */}
                              {visibleStatusChips.map(chip => (
                                <Chip
                                  key={chip.label}
                                  label={chip.label}
                                  size="small"
                                  color={chip.color === 'default' ? 'default' : chip.color}
                                  variant={chip.color === 'default' ? 'outlined' : 'filled'}
                                  sx={{ fontSize: '0.62rem', height: 18, fontWeight: 500 }}
                                />
                              ))}
                            </Box>
                            {operationalSummary && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}
                              >
                                {operationalSummary}
                              </Typography>
                            )}
                          </TableCell>
                          {showWineColumns && (
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {item.wineDetails?.wineDisplay?.producerName ?? '—'}
                              </Typography>
                            </TableCell>
                          )}
                          {showWineColumns && (
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {item.wineDetails?.wineDisplay?.displayVintage ?? '—'}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.displaySnapshot?.subtitle || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {sectionName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.visibility}
                              size="small"
                              color={item.visibility === 'LIVE' ? 'success' : 'default'}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatItemPrice(item)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" onClick={e => e.stopPropagation()}>
                            <Tooltip title="Edit item">
                              <IconButton size="small" onClick={() => navigate(`${base}/${menu._id}/edit/${item._id}`)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete item">
                              <IconButton size="small" color="error" onClick={e => handleDeleteItem(e, item)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={itemsPagination.totalItems}
              page={itemsPagination.page}
              rowsPerPage={itemsPagination.pageSize}
              onPageChange={(_e, newPage) => setItemsPagination(prev => ({ ...prev, page: newPage }))}
              onRowsPerPageChange={e => setItemsPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        )}

        {tab === 'sections' && (
          <Box>
            <Box sx={{ px: 3, pt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Sections organize how items appear to guests.
              </Typography>
            </Box>
            <MenuSectionsPanel venueId={venueId} menuId={menu._id} />
          </Box>
        )}

        {/* MENU-EXEC.2 — "Serving sizes" (MenuFormatsPanel) and "Recipes"
            (MenuRecipesPanel) render branches removed. Components/services/routes
            are retained for now; they are simply not surfaced in the menu UI. */}

        {tab === 'preview' && (
          <MenuPreviewPanel venueId={venueId} menuId={menu._id} />
        )}

        {tab === 'share' && (
          <Box sx={{ p: 3 }}>
            <MenuPublishPanel venueId={venueId} menuId={menu._id} menuStatus={menu.status} />
          </Box>
        )}
      </Paper>

      {/* Phase G.4.a — universal add-item preset picker, shown for every
          authorable menu. Presets are helpers/defaults, not item architectures;
          each routes to the same /add?kind=<KIND> contract the dispatcher reads.
          The menu type's closest preset is marked "Suggested" but never forced. */}
      <Dialog open={pickKindOpen} onClose={() => setPickKindOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>What are you adding?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Pick a preset to start. Presets just set helpful defaults — every item uses the same form.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
            {ADD_ITEM_PRESETS.map(opt => {
              const suggested = opt.kind === suggestedAddKind;
              return (
                <Button
                  key={opt.kind}
                  variant={suggested ? 'contained' : 'outlined'}
                  onClick={() => {
                    setPickKindOpen(false);
                    navigate(`${base}/${menu._id}/add?kind=${opt.kind}`);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600, minWidth: 130, flex: '1 1 130px' }}
                >
                  {suggested ? `${opt.label} · Suggested` : opt.label}
                </Button>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickKindOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuDetail;
